import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { getErrorMessage } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import {
  Package,
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  AlertTriangle,
  Filter,
  Power,
  PowerOff,
  Download,
} from "lucide-react";

const ProductsPage = () => {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    unit: "",
    min_stock_alert: 0,
    image_url: "",
  });

  useEffect(() => {
    // Read status from URL params
    const urlStatus = searchParams.get("status");
    if (urlStatus) {
      setStatusFilter(urlStatus);
    }
    fetchProducts();
    fetchCategories();
    fetchUnits();
  }, [searchParams]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (categoryFilter) params.append("category", categoryFilter);
      
      const response = await api.get(`/products?${params.toString()}`);
      // Filter by status on frontend
      let filteredProducts = response.data;
      if (statusFilter === "active") {
        filteredProducts = response.data.filter(p => p.is_active !== false);
      } else if (statusFilter === "inactive") {
        filteredProducts = response.data.filter(p => p.is_active === false);
      } else if (statusFilter === "low_stock") {
        // Filtrar produtos com estoque baixo (atual <= mínimo)
        filteredProducts = response.data.filter(p => 
          p.is_active !== false && 
          p.current_stock <= (p.min_stock_alert || 0)
        );
      }
      setProducts(filteredProducts);
    } catch (error) {
      toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Buscar todas as categorias do sistema (incluindo as sem produtos)
      const response = await api.get("/settings/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await api.get("/settings/units");
      setUnits(response.data);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchProducts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm, categoryFilter, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, formData);
        toast.success("Produto atualizado com sucesso!");
      } else {
        await api.post("/products", formData);
        toast.success("Produto criado com sucesso!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
      fetchCategories();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao salvar produto"));
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      await api.put(`/products/${product.id}/status`, { is_active: !product.is_active });
      toast.success(product.is_active ? "Produto desativado!" : "Produto ativado!");
      fetchProducts();
    } catch (error) {
      toast.error("Erro ao alterar status do produto");
    }
  };

  const exportToCSV = () => {
    if (products.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }
    
    const headers = ["Nome", "Categoria", "Unidade", "Estoque Atual", "Estoque Mínimo", "Status"];
    const csvContent = [
      headers.join(","),
      ...products.map(p => [
        `"${p.name}"`,
        `"${p.category}"`,
        p.unit,
        p.current_stock,
        p.min_stock_alert,
        p.is_active !== false ? "Ativo" : "Inativo"
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `produtos_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      category: product.category,
      unit: product.unit,
      min_stock_alert: product.min_stock_alert,
      image_url: product.image_url || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Deseja excluir o produto "${product.name}"?`)) return;
    
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Produto excluído com sucesso!");
      fetchProducts();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erro ao excluir produto"));
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      unit: "",
      min_stock_alert: 0,
      image_url: "",
    });
  };

  const openNewDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const isLowStock = (product) => {
    return product.current_stock <= product.min_stock_alert;
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
            Produtos
          </h1>
          <p className="text-slate-500 mt-1">
            Gerencie os produtos do seu estoque
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} className="gap-2" data-testid="export-products-btn">
            <Download className="h-4 w-4" />
            Exportar
          </Button>
          {hasPermission("edit") && (
            <Button onClick={openNewDialog} className="gap-2" data-testid="add-product-btn">
              <Plus className="h-4 w-4" />
              Novo Produto
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar produtos pelo nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-products-input"
              />
            </div>
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as Categorias</SelectItem>
                {categories.filter(cat => cat.name && cat.name.trim()).map((cat) => (
                  <SelectItem key={cat.id || cat.name} value={cat.name}>
                    {cat.name} ({cat.product_count || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="low_stock">Estoque Baixo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="card-dashboard">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Package className="h-12 w-12 mb-4 text-slate-300" />
              <p>Nenhum produto encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead className="text-right">Estoque Atual</TableHead>
                    <TableHead className="text-right">Mín. Alerta</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id} className={`table-row-zebra ${product.is_active === false ? 'opacity-60' : ''}`} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {product.image_url ? (
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Package className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{product.name}</p>
                            {product.description && (
                              <p className="text-sm text-slate-500 truncate max-w-xs">
                                {product.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{product.category}</Badge>
                      </TableCell>
                      <TableCell>{product.unit}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isLowStock(product) && product.is_active !== false && (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          )}
                          <span className={isLowStock(product) && product.is_active !== false ? "text-amber-600 font-semibold" : ""}>
                            {product.current_stock}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{product.min_stock_alert}</TableCell>
                      <TableCell>
                        {product.is_active !== false ? (
                          <Badge className="alert-badge-success">Ativo</Badge>
                        ) : (
                          <Badge className="alert-badge-danger">Inativo</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`product-actions-${product.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {hasPermission("edit") && (
                              <DropdownMenuItem onClick={() => handleEdit(product)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {hasPermission("edit") && (
                              <DropdownMenuItem onClick={() => handleToggleStatus(product)}>
                                {product.is_active !== false ? (
                                  <>
                                    <PowerOff className="mr-2 h-4 w-4" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <Power className="mr-2 h-4 w-4" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {hasPermission("delete") && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(product)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingProduct ? "Editar Produto" : "Novo Produto"}
            </DialogTitle>
            <DialogDescription>
              {editingProduct
                ? "Atualize as informações do produto"
                : "Preencha os dados do novo produto"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Produto *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="product-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Categoria *</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Ex: Preenchedor"
                    required
                    list="categories-list"
                    data-testid="product-category-input"
                  />
                  <datalist id="categories-list">
                    {categories.map((cat) => (
                      <option key={cat.name} value={cat.name} />
                    ))}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unidade *</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger data-testid="product-unit-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.filter(unit => unit.abbreviation && unit.abbreviation.trim()).map((unit) => (
                        <SelectItem key={unit.id} value={unit.abbreviation}>
                          {unit.name} ({unit.abbreviation})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_stock_alert">Estoque Mínimo para Alerta</Label>
                <Input
                  id="min_stock_alert"
                  type="number"
                  min="0"
                  value={formData.min_stock_alert}
                  onChange={(e) =>
                    setFormData({ ...formData, min_stock_alert: parseInt(e.target.value) || 0 })
                  }
                  data-testid="product-min-stock-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image_url">URL da Imagem (opcional)</Label>
                <Input
                  id="image_url"
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-image-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="product-submit-btn">
                {editingProduct ? "Salvar" : "Criar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
